import { useTranslation } from 'react-i18next';
import BulkEditor from './BulkEditor';
import LocationAwareSearch from '../../../../components/LocationAwareSearch';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from '../../../../hooks';
import { useAsync } from 'react-async';
import { getTripTemplates } from '../../../../api/node/trip';
import { isEqual } from 'lodash';
import { KoobInputSegmented, KoobPagination } from '@koob/ui';
import { OrderColumnEnum, OrderDirectionEnum } from '@koob/enums';
import { useKoobStore } from '../../../../store';
import useBulkEditExcludedIds from './useBulkEditExcludedIds';
import { matchesSearchWords, toSearchWords } from '@koob/text-search';

export default function BulkEditorTemplates() {
  const { t } = useTranslation('toConnectionRequest');
  const [{ search }] = useSearchParams();

  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(48);
  const [totalPages, setTotalPages] = useState();

  const toConnectionRequestModel = useKoobStore(
    (state) => state.toConnectionRequestModel,
  );
  const [showOnlySelected, setShowOnlySelected] = useState(
    toConnectionRequestModel ? true : null,
  );

  const excludedIds = useBulkEditExcludedIds({
    fieldName: 'toConnectionRequestTemplates',
    showOnlySelected,
  });
  const excludedIdsKey = excludedIds?.join(',');

  const requestFilters = {
    search: search ?? '',
    onlyFavorites,
    ...(excludedIds && { excludedIds }),
    perPage: perPage,
    page,
    direction: OrderDirectionEnum.DESC,
    order: OrderColumnEnum.UPDATED_AT,
  };

  useEffect(() => {
    setPage(1);
  }, [search, onlyFavorites, excludedIdsKey]);

  const { data, isLoading: loading } = useAsync({
    promiseFn: getTripTemplates,
    filters: requestFilters,
    watchFn: (props, prevProps) => {
      return !isEqual(props.filters, prevProps.filters);
    },
    onResolve: (response) => {
      setTotalPages(response?.headers?.xTotalPages);
    },
  });

  const totalCount = data?.headers?.xTotalCount;
  const templates = data?.data ?? [];

  // Only the "selected" view filters in the browser: it lists the local
  // selection, which the server never sees. Server pages are shown as they
  // come, and the search mirrors the server's (name or KOOB id, KD-3530).
  const searchWords = useMemo(() => toSearchWords(search), [search]);
  const filterResult = useCallback(
    (item) => matchesSearchWords(searchWords, [item.name], item.id),
    [searchWords],
  );

  return (
    <BulkEditor
      title={t('bulkEdit.editor.templatesTitle')}
      filters={
        <>
          <LocationAwareSearch
            name="search"
            placeholder={t('misc:searchByNameOrId')}
          />

          <KoobInputSegmented
            options={[
              { label: t('experiences:state.all'), value: false },
              { label: t('tripDesigner:search.onlyFavorites'), value: true },
            ]}
            value={requestFilters.onlyFavorites ?? null}
            onChange={(value) => setOnlyFavorites(value)}
          />
        </>
      }
      loading={loading}
      results={templates.map((template) => ({
        id: template.id,
        name: template.name,
        description: template.description,
        image: template.imageUrl,
        state: template.state,
      }))}
      filterResult={filterResult}
      type="template"
      totalCount={
        excludedIds ? Number(totalCount ?? 0) + excludedIds.length : totalCount
      }
      onShowOnlySelectedChange={setShowOnlySelected}
      perPage={perPage}
      setPage={setPage}
      setTotalPages={setTotalPages}
      totalPages={data?.headers?.xTotalPages}
    >
      {templates.length > 0 && (
        <KoobPagination
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          totalCount={totalCount}
          perPage={perPage}
          setPerPage={setPerPage}
        />
      )}
    </BulkEditor>
  );
}
